import { useCallback } from "react";
import { Validation, isAnyNestedHandler } from "@madhive/mad-sdk";
import { getSingularDataObservable } from "./singularUtils";
import { getHandler } from "../../../utils/collections";
/**
 * Normalizes a validate result (Promise<Validation> | Validation | string) into a Promise<Validation>.
 */
export const getSingularValidations = (validate, key) => {
    const keyArr = [];
    if (Array.isArray(key)) {
        keyArr.push(...key);
    }
    else if (key) {
        keyArr.push(key);
    }
    if (validate instanceof Promise) {
        return new Promise((resolve) => {
            validate.then((validated) => {
                if (typeof validated === "string") {
                    const validation = new Validation();
                    keyArr.forEach((k) => validation.set(k, validated));
                    return resolve(validation);
                }
                return resolve(validated);
            });
        });
    }
    if (typeof validate === "string") {
        const validation = new Validation();
        keyArr.forEach((k) => validation.set(k, validate));
        return Promise.resolve(validation);
    }
    return Promise.resolve(validate);
};
/**
 * Returns functions for getting/setting validationErrors for provided data.
 * @param collection Collection the data belongs to
 * @param id The id of the data
 * @returns a Promise
 */
export const useValidation = (handler, observer, options = {}) => {
    const getKeysToValidate = ({ keys, changes = observer.value.deltas }) => {
        return handleGettingKeysToValidate(handler, keys, changes) || [];
    };
    const validateFunction = useCallback((validatorKey, clearAll) => handleValidateSingularData(observer, handler, validatorKey, clearAll, options.parent ? { data: options.parent.data, deltas: options.parent.deltas } : undefined), [observer, handler, options.parent?.data]);
    return { validateFunction, getValidations: getSingularValidations, getKeysToValidate };
};
export function validateSingularData(collection, id, { keys, clearAll, observer: _observer } = {}) {
    const observer = _observer || getSingularDataObservable(collection, id);
    const handler = getHandler(collection);
    return handleValidateSingularData(observer, handler, keys, clearAll);
}
export function getSingularKeysToValidate(collection, id, { changes, observer: _observer, keys } = {}) {
    const observer = _observer || getSingularDataObservable(collection, id);
    const changesToCheck = changes || observer.value.deltas || {};
    const handler = getHandler(collection);
    return handleGettingKeysToValidate(handler, keys, changesToCheck) || [];
}
function handleGettingKeysToValidate(handler, keys, changes) {
    const keysArr = typeof keys === "string" ? [keys] : keys || Object.keys(changes || {});
    if (!keys && !keysArr.length) {
        return undefined;
    }
    const keysToValidate = new Set(keysArr);
    keysToValidate.forEach((key) => {
        handler.getDependantValidatorKeys(key)?.forEach((dependant) => {
            keysToValidate.add(String(dependant));
        });
    });
    return [...keysToValidate];
}
/**
 * Standalone (non-hook) function that validates a singular data entry.
 */
function handleValidateSingularData(observer, handler, validatorKey, clearAll, parentData) {
    return new Promise((resolve) => {
        const current = observer.value;
        const keys = handleGettingKeysToValidate(handler, validatorKey, current.deltas);
        const validationErrors = !clearAll && keys ? current.validationErrors || new Validation() : new Validation();
        observer.next({
            ...observer.value,
            isValidating: keys
        });
        if (keys) {
            keys.forEach((key) => validationErrors.delete(key));
        }
        const changesToValidate = {
            ...current.data,
            ...current.deltas
        };
        if (isAnyNestedHandler(handler) && parentData) {
            changesToValidate[handler.getKeyToPutParentOn()] = {
                ...parentData.data,
                ...parentData.deltas
            };
        }
        const validate = handler.validate(changesToValidate, keys);
        getSingularValidations(validate).then((validations) => {
            validations.forEach((value, key) => {
                validationErrors.set(key, value);
            });
            observer.next({
                ...observer.value,
                validationErrors,
                isValidating: false
            });
            resolve(validationErrors);
        });
    });
}
//# sourceMappingURL=useValidation.js.map